import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { site } from '@/data/site';
import { sections } from '@/data/sections';
import { useScrolled } from '@/hooks/useScrolled';
import { useActiveSection } from '@/hooks/useActiveSection';
import { Magnetic } from '@/components/motion/Magnetic';

const IDS = sections.map((s) => s.id);
/** 'intro' is the page top, reachable by the wordmark — not a nav link. */
const LINKS = sections.filter((s) => s.id !== 'intro');

export function Nav() {
  const scrolled = useScrolled(24);
  const active = useActiveSection(IDS);
  const [menuOpen, setMenuOpen] = useState(false);

  // A full-screen menu that scrolls the page behind it is disorienting, and
  // Escape must always close it — both are baseline expectations, not polish.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={cn(
          'ease-settle fixed inset-x-0 top-0 z-50 transition-colors duration-300',
          scrolled && 'bg-void/80 border-edge border-b backdrop-blur-md',
        )}
      >
        <nav
          aria-label="Primary"
          className="flex items-center justify-between px-(--gutter) py-4"
        >
          <a href="#intro" className="label text-bone hover:text-acid transition-colors">
            {site.name}
          </a>

          <ul className="hidden items-center gap-6 lg:flex">
            {LINKS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  aria-current={active === s.id ? 'true' : undefined}
                  className={cn(
                    'label hover:text-bone transition-colors',
                    active === s.id && 'text-acid',
                  )}
                >
                  {s.label}
                </a>
              </li>
            ))}
            <li>
              <Magnetic>
                <a
                  href={site.resumePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="label border-acid text-acid hover:bg-acid hover:text-void inline-block border px-3 py-1.5 transition-colors"
                >
                  Résumé
                </a>
              </Magnetic>
            </li>
          </ul>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className="label text-bone hover:text-acid transition-colors lg:hidden"
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
        </nav>
      </header>

      {/* Kept mounted-on-open rather than always-rendered-and-hidden: a
          visually hidden nav that still holds focus order is a real keyboard
          trap, and `hidden` alone would not animate anyway. */}
      {menuOpen && (
        <div
          id="mobile-menu"
          className="bg-void fixed inset-0 z-45 flex flex-col justify-center px-(--gutter) lg:hidden"
        >
          <ul className="space-y-2">
            {LINKS.map((s) => (
              <li key={s.id} className="border-edge border-b">
                <a
                  href={`#${s.id}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-baseline gap-4 py-3"
                >
                  <span className="label text-acid">{s.number}</span>
                  <span className="display text-title">{s.label}</span>
                </a>
              </li>
            ))}
          </ul>
          <a
            href={site.resumePath}
            target="_blank"
            rel="noopener noreferrer"
            className="label border-acid text-acid mt-8 self-start border px-4 py-2"
          >
            Résumé
          </a>
        </div>
      )}
    </>
  );
}
